import { z } from "zod";

/**
 * The onboarding-and-retention wave's wire contracts.
 *
 * WHY `contact_email` IS NOT `users.email`. `users.email` is the AUTH address,
 * and since X is the only way in (ruling R-0826-1) nobody has a real one:
 * `@x.placeholder.nob.me` is a Better Auth routing key, not a mailbox.
 * `contact_email` is the separate, single answer to "where does Nob
 * write to YOU": the Morning Nob, the routing alert, the channel confirmation.
 *
 * IT IS NO LONGER OPTIONAL AND NO LONGER A CLAIM (ruling R-0826-3). It is asked
 * for inside the onboarding run, as a step of its own between Connect and the
 * money step, and the run does not advance until a six-digit code has come back
 * from the mailbox. "Required softly" was the old shape — a setup prompt that
 * could be ignored for ever — and it produced accounts the mandatory mail could
 * not reach at all.
 *
 * There is deliberately no way to UNSET it. Changing where the post goes is an
 * ordinary edit — and it goes through the same code — but emptying the
 * letterbox is not a setting, it is a silence the owner would discover a week
 * later by not hearing anything.
 */
export const ContactEmailRequest = z.object({
  // Normalised BEFORE it is judged, not after: a pasted address arrives with a
  // trailing space and a capitalised first letter about as often as it arrives
  // clean, and refusing that is refusing a correct address for being untidy.
  // Lowercased on the way in so the stored value and every later comparison
  // agree without a `lower()` anywhere. 320 is the RFC 5321 practical maximum
  // (64 local + @ + 255 domain); past it this is a payload, not an address.
  email: z.string().trim().toLowerCase().pipe(z.email().max(320)),
  /**
   * THE AGE ATTESTATION RIDES WITH THE ADDRESS (ruling R-0914-1 (1)), and it is
   * optional HERE and required by the route. The two are not the same thing.
   *
   * This endpoint has two entry points — the wizard's Email step and the
   * account page's Change — and only the first of them is where somebody says
   * how old they are. Making the field mandatory in the schema would mean the
   * account page posting a consent nobody ticked at the time, which is the
   * exact failure a hashed consent exists to prevent. So the shape allows its
   * absence and `onboardingFlow.ts` refuses an account that has not attested
   * yet with `AGE_ATTESTATION_REQUIRED`.
   *
   * The TEXT never crosses the wire, only the SHA-256 of what was shown, the
   * same way a purchase consent travels (`plans/disclosure.ts`).
   */
  age_attested: z.boolean().optional(),
  age_attestation_hash: z.string().min(1).optional(),
});
export type ContactEmailRequest = z.infer<typeof ContactEmailRequest>;

/** The code's length, its expiry, its attempt ceiling and its resend floor. */
export const EMAIL_CODE_LENGTH = 6;
export const EMAIL_CODE_TTL_MINUTES = 10;
export const EMAIL_CODE_MAX_ATTEMPTS = 5;
export const EMAIL_CODE_RESEND_SECONDS = 60;

/**
 * The code coming back (`POST /api/v1/onboarding/email/confirm`).
 *
 * Digits only, and exactly six of them, checked before anything is looked up:
 * a body that cannot be a code is a 400 rather than a wasted attempt off the
 * five somebody gets. Spaces are stripped because mail clients and humans both
 * put them in — `123 456` is the same code, and refusing it would be refusing a
 * correct answer for its punctuation.
 */
export const EmailCodeRequest = z.object({
  code: z
    .string()
    .transform((c) => c.replace(/[\s-]/g, ""))
    .pipe(z.string().regex(new RegExp(`^\\d{${EMAIL_CODE_LENGTH}}$`))),
});
export type EmailCodeRequest = z.infer<typeof EmailCodeRequest>;

/**
 * The doorman-thread prompts (FR-11), in the fixed order they are shown.
 *
 * THIS LIST IS THE WHOLE VOCABULARY. A name that is not one of these two is
 * discarded wherever it arrives from — a client body, a route parameter, a
 * model's suggestion — for the same reason the grilling model is given no
 * capability at all: an enumeration that can be extended at runtime is not an
 * enumeration, and "mark this done" is a write.
 *
 * `set_contact_email` USED TO BE THE FIRST OF THREE and is deleted (ruling
 * R-0826-3). The address is now collected inside the onboarding run and
 * verified before the run advances, so by the time a doorman thread exists the
 * job is already done — a prompt that is born answered is a prompt that only
 * ever renders as a bug.
 */
export const PROMPT_ACTIONS = ["share_door", "edit_rules"] as const;
export type PromptAction = (typeof PROMPT_ACTIONS)[number];

export function isPromptAction(value: unknown): value is PromptAction {
  return typeof value === "string" && (PROMPT_ACTIONS as readonly string[]).includes(value);
}

/** The 30-day life of a held draft, in days — the TTL every write refreshes. */
export const HELD_DRAFT_TTL_DAYS = 30;

/** Matches the `held_drafts_body_len` CHECK; the column is the floor under this. */
export const HELD_DRAFT_MAX_CHARS = 4000;

/**
 * `POST /api/v1/draft` — the pre-account knock, written by somebody who has not
 * signed in and may never. The `anon_key` is a client-minted uuid held in the
 * browser: it is the ONLY key there is, so it is also the only secret, which is
 * why a miss and an expiry answer identically (`{body: null}`) and why nothing
 * here ever lists or counts.
 */
export const HeldDraftRequest = z.object({
  anon_key: z.uuid(),
  door_handle: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .transform((h) => h.replace(/^@/, "")),
  body: z.string().min(1).max(HELD_DRAFT_MAX_CHARS),
});
export type HeldDraftRequest = z.infer<typeof HeldDraftRequest>;

/**
 * THE DOOR STEP (PRD §2.2.5, FR-1.1 step 3, ruling C5).
 *
 * Three questions, all answered by tapping, and each answer resolves into a
 * SENTENCE in `nob.md` rather than into a stored enum (PRD §2.2.5). The ids
 * below are the whole vocabulary — the prose they resolve to lives on the
 * server (`rules/starter.ts`) and travels to the client as text, so there is
 * one copy of every sentence and the client renders what it is given.
 */
export const INTEREST_IDS = ["funding", "hiring", "bd", "built", "press", "advice"] as const;
export type InterestId = (typeof INTEREST_IDS)[number];

export const FILTER_LEVELS = ["easy", "medium", "hard"] as const;
export type FilterLevel = (typeof FILTER_LEVELS)[number];

/** `less` · `medium` · `more` — WEEKLY, DAILY, REALTIME in the file's own words. */
export const NOTIFY_LEVELS = ["less", "medium", "more"] as const;
export type NotifyLevel = (typeof NOTIFY_LEVELS)[number];

/**
 * `POST /api/v1/onboarding/door` — the door step completing. It TUNES the door
 * the email step already hung (ruling R-1006-2): the answers become a new rules
 * version. Every answer is optional; the defaults compose a working door
 * (FR-1.2 #5).
 */
export const DoorStepRequest = z.object({
  interests: z.array(z.enum(INTEREST_IDS)).max(INTEREST_IDS.length).default([]),
  filter_level: z.enum(FILTER_LEVELS).default("medium"),
  notify_level: z.enum(NOTIFY_LEVELS).default("medium"),
});
export type DoorStepRequest = z.infer<typeof DoorStepRequest>;
